import { useEffect } from "react";
import { useServiceWorkerUpdate } from "@/hooks/useServiceWorkerUpdate";
import { setSentryUpdateState } from "@/sentry";
import { UpdateReadyBanner } from "./UpdateReadyBanner";

/**
 * The mount point for the update affordance — and nothing else.
 *
 * This component used to call `useRegisterSW` from `virtual:pwa-register/react`,
 * which registered a *second* service-worker client, installed the plugin's
 * unconditional `activated → window.location.reload()` listener (no opt-out
 * exists), and leaked an hourly `setInterval` that no unmount ever cleared. All
 * three are gone: registration, the trigger schedule and the safe-apply policy
 * now live in `lib/pwa/serviceWorkerClient`, initialized once from `main.tsx`
 * before `root.render()`. Do not reintroduce a registration here — outside
 * React is the only place `<StrictMode>` cannot double it.
 *
 * Its inputs are module-level external stores rather than `useSyncStatus()`,
 * which throws outside `SyncStatusProvider`. That is what lets it mount above
 * the `bootState` early returns, so a client sitting on the offline splash
 * screens still learns about a new build.
 */
export function ServiceWorkerAutoUpdate() {
	const { state, applyUpdate } = useServiceWorkerUpdate();

	// One tag, valued from the closed `UpdateState` union — never interpolated
	// free text, never anything read from a form. It is what makes a stale
	// cohort visible in Sentry without carrying a byte of user data.
	useEffect(() => {
		setSentryUpdateState(state);
	}, [state]);

	return <UpdateReadyBanner state={state} onApply={applyUpdate} />;
}
